/* =============================================================================
   VIEWS / MENU-PICKER-HELPERS — Pure Helpers (Menu-Filter + Preis-Berechnung)
   =============================================================================
   S69 REF-4 Cut 1/3: aus menu-picker.js (1.354 LOC) extrahiert.

   Alle Funktionen hier sind **pure** — sie nehmen `menu` (Array) + andere
   Argumente, ohne `_state` zu lesen. Das macht sie test-freundlich und
   wiederverwendbar.

   Enthält:
     - itemsByCategoryFromMenu(menu, category, dietFilter, opts)
     - itemsBySubcategoryFromMenu(menu, category, dietFilter)
     - optionsByGroupFromMenu(menu, group)
     - addonsByGroupFromMenu(menu, group, type)
     - findItemFromMenu(menu, id)
     - dietTagBadges(tags)
     - recalcLine(line)        — mutates + returns line.lineTotal
     - subtotalOf(lines)       — pure Summe der lineTotals

   Das Hauptmodul (menu-picker.js) wrappt diese Funktionen state-aware:
     itemsByCategory(category, opts) = itemsByCategoryFromMenu(getMenu(),
       category, _state.dietFilter, opts)
   Damit bleiben Test-Hook-Re-Exports (`_itemsByCategory` etc.) und alle
   Render/Action-Aufrufer unverändert.
============================================================================ */

import { htmlEscape } from '../utils/helpers.js';
// S266 — Gast-Kanäle: Server-Ablehnung CHANNEL_DISABLED:<key> in Gast-Sprache.
import { channelDisabledKeyFromError, guestChannelDisabledMessage } from '../core/guest-channels.js';

// -----------------------------------------------------------------------------
// Helpers: Kategorisierung + Diät-Filter
// -----------------------------------------------------------------------------
export function itemsByCategoryFromMenu(menu, category, dietFilter, opts) {
  opts = opts || {};
  const filter = dietFilter || 'all';
  return (menu || [])
    .filter(m => m.available && m.category === category)
    .filter(m => {
      if (opts.includeOptionType) return true;
      return m.itemType !== 'option';
    })
    .filter(m => {
      if (filter === 'all') return true;
      if (filter === 'vegan')        return (m.dietaryTags || []).includes('vegan');
      if (filter === 'vegetarisch')  return (m.dietaryTags || []).includes('vegan') || (m.dietaryTags || []).includes('vegetarisch');
      if (filter === 'glutenfrei')   return true;   // nur Brötchen-Optionen werden gefiltert; Hauptgerichte zeigen den Hinweis selbst
      if (filter === 'halal')        return (m.dietaryTags || []).includes('halal') || (m.dietaryTags || []).includes('vegan') || (m.dietaryTags || []).includes('vegetarisch') || (m.dietaryTags || []).includes('fisch');
      return true;
    })
    // S187 Cut-A-Fix — Reihenfolge GARANTIEREN statt annehmen: nach display_order
    // sortieren. `.filter` liefert ein neues Array, `menu` bleibt unangetastet.
    // Damit stimmt der Gast-Renderpfad UND die Studio-Vorschau auch dann, wenn das
    // In-Memory-Menü nach einem Reorder nicht neu einsortiert wurde. (Array.sort ist
    // stabil → gleicher display_order behält Einfüge-Reihenfolge.)
    .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0));
}

export function itemsBySubcategoryFromMenu(menu, category, dietFilter) {
  // Gruppiert nach m.subcategory unter Beibehaltung der display_order.
  const items = itemsByCategoryFromMenu(menu, category, dietFilter);
  const groups = new Map();
  for (const it of items) {
    const key = it.subcategory || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(it);
  }
  // Reihenfolge nach Erst-Vorkommen der Unter-Rubrik = display_order-Reihenfolge,
  // GARANTIERT durch die Sortierung in itemsByCategoryFromMenu (nicht mehr Annahme).
  return Array.from(groups.entries());
}

export function optionsByGroupFromMenu(menu, group) {
  // S171 P5 — eine Option darf mehreren Gruppen angehören: primär meta.optionGroup
  // (String), zusätzlich optionales meta.optionGroups (Array). So lassen sich
  // bestehende Optionen (z. B. Burger-Pattys) ohne Duplikat in einer zweiten
  // Gruppe (Mittagstisch-Topping) wiederverwenden — Single-Source bleibt das Item.
  return (menu || [])
    .filter(m => m.itemType === 'option')
    .filter(m => m.meta && (
      m.meta.optionGroup === group ||
      (Array.isArray(m.meta.optionGroups) && m.meta.optionGroups.includes(group))
    ))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

export function addonsByGroupFromMenu(menu, group, type) {
  return (menu || [])
    .filter(m => m.itemType === (type || 'extra'))
    .filter(m => !group || (m.meta && m.meta.addonGroup === group))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

/**
 * S193 — Extras-Gruppe (group_key) eines Gerichts auflösen — Single-Source für
 * Render UND Action (damit die dish-driven Optionsregeln beide gleich treffen).
 * Primär meta.extrasGroup; Salate ohne eigene Gruppe leihen sich die Bowl-Extras
 * (F4-Verhalten, unverändert). @returns {string|null}
 */
export function resolveExtrasGroup(menu, item) {
  const meta = (item && item.meta) || {};
  let group = meta.extrasGroup;
  if (!group && meta.isSalad) {
    for (const m of (menu || [])) {
      if (m.meta && m.meta.isBowl && m.meta.extrasGroup) { group = m.meta.extrasGroup; break; }
    }
  }
  return group || null;
}

export function findItemFromMenu(menu, id) {
  return (menu || []).find(m => m.id === id) || null;
}

/**
 * S229 T1 — tolerante Beilagen-Liste einer Zeile/Position. Neuer Single-Source-Key
 * ist `sides` (Array, Mehrfach-Beilagen im Normal-Bestellvorgang). Legacy-/Deal-
 * Zeilen tragen die EINE Beilage weiterhin im Objekt-Key `side` → hier tolerant
 * mitlesen, damit Alt-Orders + Deal-Positionen unverändert rendern/rechnen.
 * @returns {Array<{id,name,price}>}
 */
export function sidesOf(line) {
  if (!line) return [];
  if (Array.isArray(line.sides)) return line.sides;
  if (line.side) return [line.side];
  return [];
}

export function dietTagBadges(tags) {
  if (!Array.isArray(tags) || tags.length === 0) return '';
  return tags.map(t => {
    const cls = 'mp-tag mp-tag-' + t.replace(/[^a-z0-9_]/gi, '');
    return `<span class="${cls}">${htmlEscape(t)}</span>`;
  }).join('');
}

// -----------------------------------------------------------------------------
// Preis-Berechnung
// -----------------------------------------------------------------------------
/** Mutiert `line.lineTotal` in-place und gibt den Wert zurück.
 *  Behält die ursprüngliche Semantik aus menu-picker.js bei. */
export function recalcLine(line) {
  // S169 P3-Rework — Deal-Position: Preis = Gruppen-Deal-Preis + optionale Add-on-
  // Stufen (Beilage/Getränk). Brötchen/Extras/Soßen sind im Deal-Preis INKLUSIVE
  // (0 €). Guarded über line.deal → Normal-Zeilen unverändert.
  if (line.deal) {
    // S169-Korrektur: Beilage liegt im NORMALEN Key line.side (im Deal-Preis
    // eingerechnet), Drink separat. Beilagen-Dips (line.sideSauces) sind inkl. (0 €).
    const dealBase   = (Number(line.deal.dealPriceCents) || 0) / 100;
    const extras     = (line.extras || []).reduce((s, e) => s + (Number(e.price) || 0), 0); // Extras on top
    const sidePrice  = (line.side      && Number(line.side.price))      || 0;
    const drinkPrice = (line.dealDrink && Number(line.dealDrink.price)) || 0;
    // S198-HF2 — Brötchen-Aufpreis (Whitelist, z. B. glutenfrei) einrechnen; muss mit
    // resolveDealToOrderItems übereinstimmen (Footer-Vorschau = persistierter Preis).
    const bunFee     = (line.bun && Number(line.bun.extra)) || 0;
    line.lineTotal = +((dealBase + bunFee + extras + sidePrice + drinkPrice) * (Number(line.qty) || 1)).toFixed(2);
    return line.lineTotal;
  }
  const base       = Number(line.unitPrice) || 0;
  const bunFee     = (line.bun && Number(line.bun.extra)) || 0;
  const extras     = (line.extras || []).reduce((s, e) => s + (Number(e.price) || 0), 0);
  const sauces     = (line.sauces || []).reduce((s, e) => s + (Number(e.price) || 0), 0);
  // S229 T1 — Normal-Zeile: Summe ÜBER ALLE Beilagen (Mehrfachauswahl). Deal-Zweig
  // oben bleibt Single (line.side). sidesOf liest tolerant sides[] bzw. Legacy-side.
  const sidePrice  = sidesOf(line).reduce((s, x) => s + (Number(x.price) || 0), 0);
  const sideSauces = (line.sideSauces || []).reduce((s, e) => s + (Number(e.price) || 0), 0);
  line.lineTotal = +((base + bunFee + extras + sauces + sidePrice + sideSauces) *
                     (Number(line.qty) || 1)).toFixed(2);
  return line.lineTotal;
}

/** Pure: addiert `lineTotal` über alle übergebenen Lines. */
export function subtotalOf(lines) {
  return +(lines || []).reduce((s, l) => s + (l.lineTotal || 0), 0).toFixed(2);
}

// -----------------------------------------------------------------------------
// S244 Cut B — Picker-Zeile → Warenkorb-Anfrage für den Tisch-Pfad
// -----------------------------------------------------------------------------
/**
 * Übersetzt eine fertig konfigurierte Picker-Zeile in die Anfrage, die
 * `table_cart_add` erwartet.
 *
 * Der Unterschied zu `lineToRpcItem` ist der ganze Punkt dieser Funktion: dort
 * wandert der im Browser gerechnete Preis mit, hier NUR IDs und Mengen. Der
 * Server (`price_order_line`) setzt Namen und Preise selbst aus `menu_items`
 * bzw. `deal_groups`/`deal_addons` zusammen — ein manipulierter Client kann
 * damit nichts verschieben.
 *
 * Für Deals reist die `group_id` mit: `_dealMeta` trägt nur {id,name,line_uid},
 * und ohne die Gruppe liesse sich der Deal-Preis serverseitig nicht
 * rekonstruieren.
 *
 * Pure — kein DOM, kein State.
 */
/**
 * Pure — Server-Fehler einer Gast-Bestellung in Gast-Sprache (S256).
 *
 * Die Präfixe (`PRICE_MISMATCH:` …) sind fürs Log gedacht, nicht für jemanden,
 * der gerade bestellen will. Der Preis-Fall bekommt bewusst eine Bitte um
 * Neuladen statt einer Zahl: weicht die Anzeige vom Server ab, hat der Gast
 * eine veraltete Karte gesehen — die Zahl zu nennen hülfe ihm nicht.
 */
export function friendlyOrderError(err) {
  const msg = String((err && (err.message || err.hint)) || '');
  // S266 — Gast-Kanal wurde geschlossen (CHANNEL_DISABLED:<key>, phase_196).
  const channelKey = channelDisabledKeyFromError(err);
  if (channelKey) return guestChannelDisabledMessage(channelKey);
  if (/PRICE_MISMATCH/.test(msg))
    return 'Die Preise haben sich gerade geändert. Bitte die Seite neu laden und noch einmal bestellen.';
  if (/CLIENT_OUTDATED/.test(msg))
    return 'Diese Seite ist nicht mehr aktuell. Bitte neu laden und die Bestellung erneut absenden.';
  if (/DELIVERY_MIN_ORDER/.test(msg)) {
    const m = msg.match(/Mindestbestellwert (\d+) Cent/);
    return m
      ? `Der Mindestbestellwert für die Lieferung liegt bei ${(Number(m[1]) / 100).toFixed(2).replace('.', ',')} €.`
      : 'Der Mindestbestellwert für die Lieferung ist noch nicht erreicht.';
  }
  if (/DELIVERY_ZONE_UNKNOWN/.test(msg))  return 'Zu dieser Postleitzahl liefern wir leider nicht.';
  if (/ITEM_UNAVAILABLE/.test(msg))       return 'Etwas aus deiner Auswahl ist gerade leider aus.';
  if (/ITEM_UNKNOWN|OPTION_UNKNOWN/.test(msg))
    return 'Etwas aus deiner Auswahl gibt es so nicht mehr — bitte die Karte neu laden.';
  if (/DEAL_/.test(msg))                  return 'Diese Aktion ist gerade nicht verfügbar.';
  if (/Abholzeit/.test(msg))              return msg;
  if (/Bestellungen sind aktuell deaktiviert/.test(msg))
    return 'Wir nehmen gerade keine Bestellungen an.';
  return 'Die Bestellung konnte nicht angelegt werden. Bitte noch einmal versuchen.';
}

/**
 * Pure — Steuersatz einer Zeile aus dem Artikel-Typ (S256, zusammengezogen).
 *
 * Stand vorher zweimal wortgleich in `takeaway-form.js` und `delivery-form.js`.
 * Zwei Kopien derselben Steuerregel sind eine Kopie zu viel: driften sie, hat
 * eine der beiden Strecken den falschen Satz auf dem Beleg.
 */
export function deriveTaxRateForLine(menuItem, settings) {
  const food   = Number((settings && settings.taxRateFood)   || 7);
  const drinks = Number((settings && settings.taxRateDrinks) || 19);
  const itemType = (menuItem && menuItem.itemType) || 'main';
  return itemType === 'drink' ? drinks : food;
}

/**
 * Pure — Picker-Zeile → Position für `create_customer_order` (Abholung und
 * Lieferung, S256).
 *
 * Baut auf `buildCartRequestFromLine()` auf — demselben Baustein, den der
 * Tisch-Pfad seit S244 benutzt: **nur Kennungen und Mengen**. Seit phase_187
 * bepreist der Server auch diese beiden Strecken selbst (`price_order_line`).
 *
 * Der im Browser gerechnete Preis reist weiterhin mit, aber nur noch als
 * KONTROLLWERT: der Server summiert ihn und weist die Bestellung mit
 * `PRICE_MISMATCH` ab, wenn seine eigene Summe abweicht. Was der Gast gesehen
 * hat, muss das sein, was er zahlt — still zu korrigieren wäre schlechter als
 * abzulehnen.
 *
 * `deal` reist als Objekt mit; der Server zerlegt die Aktion selbst in
 * Hauptgericht und Getränk. Vorzerlegte Deal-Komponenten (der alte Weg über
 * `getDealOrderItems()`) weist er ab — einzeln bepreist ergäben sie den
 * Normalpreis statt des Aktionspreises.
 */
export function lineToOrderRpcItem(line, menuList, settings) {
  const req = buildCartRequestFromLine(line);
  if (!req) return null;
  const menu = (menuList || []).find(m => m.id === line.ref) || null;
  const qty  = Math.max(1, Number(line.qty) || 1);
  return {
    menu_item_id:     req.menu_item_id,
    qty:              req.qty,
    notes:            req.notes,
    customizations:   req.customizations,
    ...(req.deal ? { deal: req.deal } : {}),
    // Anzeige-Werte des Clients — Kontrollwert, nicht Wahrheit.
    name:             line.name,
    category:         line.category,
    unit_price_cents: Math.round((Number(line.lineTotal) || 0) / qty * 100),
    tax_rate:         deriveTaxRateForLine(menu, settings)
  };
}

export function buildCartRequestFromLine(line) {
  if (!line || !line.ref) return null;
  const ids = (arr) => (Array.isArray(arr) ? arr : [])
    .filter(x => x && x.id).map(x => ({ id: x.id }));

  const req = {
    menu_item_id: line.ref,
    qty: Math.max(1, Math.min(20, Number(line.qty) || 1)),
    notes: (line.note && String(line.note).trim()) || null,
    customizations: {
      bun:                line.bun ? { id: line.bun.id } : null,
      patty:              line.patty || null,
      dressing:           line.dressing || null,
      milk:               line.milk || null,
      removedIngredients: line.removedIngredients || [],
      extras:             ids(line.extras),
      sauces:             ids(line.sauces),
      sideSauces:         ids(line.sideSauces)
    }
  };

  if (line.deal) {
    // Beilage im Deal: die Preis-STUFE (deal_addons) entscheidet, nicht der
    // Einzelpreis der Beilage — deshalb reist die addon_id mit.
    req.customizations.side = (line.side && line.side.id)
      ? { id: line.side.id, addon_id: line.side.addonId || null }
      : null;
    req.deal = {
      group_id: line.deal.groupId,
      line_uid: line.deal.lineUid || null,
      drink: (line.dealDrink && line.dealDrink.itemId)
        ? { addon_id: line.dealDrink.addonId, menu_item_id: line.dealDrink.itemId }
        : null
    };
  } else {
    req.customizations.sides = ids(sidesOf(line));
  }
  return req;
}
